import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

const PRINTER_SVG = (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <rect x="2" y="5" width="10" height="7" rx="1" stroke="currentColor" strokeWidth="1.4"/>
    <path d="M4 5V3a1 1 0 011-1h4a1 1 0 011 1v2" stroke="currentColor" strokeWidth="1.4"/>
    <rect x="4" y="8" width="6" height="1" rx=".5" fill="currentColor"/>
    <rect x="4" y="10" width="4" height="1" rx=".5" fill="currentColor"/>
  </svg>
);

function fmtTime(t) {
  if (!t || !/^\d{1,2}:\d{2}/.test(String(t))) return '';
  const [h, m] = String(t).split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function fmtDate(dateStr) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  } catch { return dateStr; }
}

// One print-off per DAY: pick a day, get every block that day — each section
// headed by the block's name, date, and serving times — positions with names
// and phone numbers below. Multiple pages are fine; a block never splits
// mid-section unless it's longer than a page.
export default function PrintPreviewModal({ open, onClose, blocks = [], teamName, teamPeople }) {
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape' && open) onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  // Day options from the blocks themselves: date-keyed, chronological;
  // date-less blocks (e.g. group-block teams) fall into one "No date" group.
  const days = useMemo(() => {
    const map = new Map();
    for (const b of blocks) {
      const key = b.date || b.day || 'undated';
      if (!map.has(key)) {
        map.set(key, {
          key,
          date: b.date || '',
          label: b.date ? fmtDate(b.date) : (b.day || 'No date set'),
          count: 0,
        });
      }
      map.get(key).count++;
    }
    return Array.from(map.values()).sort((a, b) => {
      if (a.date && b.date) return a.date.localeCompare(b.date);
      if (a.date) return -1;
      if (b.date) return 1;
      return a.label.localeCompare(b.label);
    });
  }, [blocks]);

  const [selKey, setSelKey] = useState(null);
  useEffect(() => {
    if (open) setSelKey(prev => (prev && days.some(d => d.key === prev)) ? prev : (days[0]?.key || null));
  }, [open, days]);

  if (!open) return null;

  const selDay = days.find(d => d.key === selKey) || null;
  const blocksFor = (key) => blocks.filter(b => (b.date || b.day || 'undated') === key);
  // "All days" prints every day in one job — each day starts on a fresh page
  // with its own date heading.
  const printAll = selKey === '__all';
  const groups = printAll
    ? days.map(d => ({ day: d, blocks: blocksFor(d.key) }))
    : (selDay ? [{ day: selDay, blocks: blocksFor(selKey) }] : []);
  const generated = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  // Rendered through a PORTAL as a direct <body> child: the print stylesheet
  // hides `body > *` and re-shows `.modal-overlay`, which only works when the
  // overlay isn't buried inside the hidden app root — otherwise the printout
  // is a blank page.
  return createPortal(
    <div
      className="modal-overlay open"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: 'var(--white)', borderRadius: 'var(--radius)', width: '95vw', maxWidth: '9.5in', maxHeight: '95vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <div>
            <div className="modal-title">{teamName} — Print Day</div>
            <div style={{ fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--muted)', marginTop: 3 }}>Preview · 8.5 × 11 in · every block on the chosen day</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <select
              className="form-input"
              style={{ width: 'auto', fontSize: 13, padding: '6px 10px', height: 'auto' }}
              value={selKey || ''}
              onChange={e => setSelKey(e.target.value)}
            >
              <option value="__all">All days ({blocks.length} block{blocks.length === 1 ? '' : 's'})</option>
              {days.map(d => (
                <option key={d.key} value={d.key}>{d.label} ({d.count} block{d.count === 1 ? '' : 's'})</option>
              ))}
            </select>
            <button className="btn btn-sm btn-dark" style={{ display: 'flex', alignItems: 'center', gap: 6 }} onClick={() => window.print()}>
              {PRINTER_SVG} Print / Save PDF
            </button>
            <button className="modal-close" onClick={onClose}>×</button>
          </div>
        </div>

        <div className="print-scroll-region" style={{ background: '#e0e0e0', padding: 24, overflowY: 'auto', flex: 1 }}>
          <div className="print-preview-page">
            <div className="print-preview-header">{teamName}</div>
            <div className="print-preview-sub">
              {printAll ? 'All days' : (selDay ? selDay.label : '')} · Belonging Conference 2026 · Generated {generated}
            </div>

            {groups.every(g => g.blocks.length === 0) && (
              <div style={{ color: 'var(--muted)', fontStyle: 'italic' }}>No blocks to print.</div>
            )}

            {groups.map(group => (
              <div key={group.day.key} className="print-day-group">
                {printAll && <div className="print-day-heading">{group.day.label}</div>}
                {group.blocks.map(block => {
              const times = [fmtTime(block.startTime), fmtTime(block.endTime)].filter(Boolean).join(' – ');
              const rows = (block.positions || []).map(pos => {
                const person = teamPeople.find(p => p.id === pos.personId);
                return {
                  pos: pos.label,
                  name: pos.open ? '— Open —' : pos.person,
                  phone: pos.open ? '' : (person?.phone || ''),
                  open: pos.open,
                };
              });
              return (
                <div key={block.id} className="print-block-section">
                  <div className="print-block-title">{block.title}</div>
                  <div className="print-block-times">
                    {[block.date ? fmtDate(block.date) : (block.day || ''), times].filter(Boolean).join(' · ') || 'No time set'}
                  </div>
                  <table>
                    <thead>
                      <tr>
                        <th>Position</th>
                        <th>Name</th>
                        <th>Phone</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr key={i}>
                          <td>{r.pos}</td>
                          <td style={{ fontFamily: 'var(--font-b)', fontSize: 14, fontWeight: 300, color: r.open ? '#aaa' : 'inherit', fontStyle: r.open ? 'italic' : 'normal' }}>{r.name}</td>
                          <td style={{ fontFamily: 'var(--font-b)', fontSize: 14, fontWeight: 300 }}>{r.phone}</td>
                        </tr>
                      ))}
                      {rows.length === 0 && (
                        <tr>
                          <td colSpan={3} style={{ color: 'var(--muted)', fontStyle: 'italic' }}>No positions yet.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
